import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
    hasInternalAdminPrivileges,
    type ActiveRoleAssignmentResponse,
    type PaymentRequestDetailsView,
} from '@pillar-portal/api-types';
import { ApiError, authApi, paymentsApi } from '@/lib/pillarApi';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PaymentRequestDetailView } from '../PaymentRequestDetailView';

export function MerchantPaymentRequestDetailContent() {
    const params = useParams();
    const paymentRequestUuid = params.uuid;

    if (paymentRequestUuid === undefined || paymentRequestUuid.trim().length === 0) {
        throw new Error('Missing payment request UUID');
    }

    const paymentRequestQuery = useQuery<PaymentRequestDetailsView>({
        queryKey: ['merchantPaymentRequestDetails', paymentRequestUuid],
        queryFn: async () => await paymentsApi.getMerchantPaymentRequestDetails(paymentRequestUuid),
        retry: false,
    });
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });

    if (paymentRequestQuery.isLoading) {
        return <div>Loading payment request details...</div>;
    }

    if (paymentRequestQuery.error !== null || paymentRequestQuery.data === undefined) {
        const errorMessage =
            paymentRequestQuery.error instanceof ApiError
                ? paymentRequestQuery.error.message
                : 'Failed to load payment request details.';

        return (
            <Alert variant="destructive">
                <AlertTitle>Unable to load payment request</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    return (
        <PaymentRequestDetailView
            paymentRequest={paymentRequestQuery.data}
            pageDescription="Merchant payment-request details"
            backHref="/payment-requests"
            backTooltip="Back to Payment Requests"
            showSupportDetails={hasInternalAdminPrivileges(activeRoleAssignmentQuery.data ?? null)}
        />
    );
}
